import * as React from 'react';
import type { AskBlock, ChatMessage, MessageAuthorKind, Visibility } from '@/types/api';
import { isKnownVisibility } from '@/types/api';
import { formatClock, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { UnknownTurn } from '@/components/unknown-turn';
import { VisibilityBand } from '@/components/visibility-band';
import { Empty } from '@/components/ui/empty';
import { BlockView } from './blocks';

/* ---------------------------------------------------------------------------
   THE THREAD.

   One conversation, two audiences. The client's turns and the expert's turns are
   the same thread and the expert reads both — and every turn the client cannot
   see carries the hatched visibility band down its full height (invariant I7).
   That is drawn as structure rather than as a badge for a specific reason: a
   reviewer scrolling a long thread has to be able to tell which half of it is
   hidden from the peripheral texture alone, because the failure this dashboard
   exists to prevent is an expert writing a request-changes note that references
   a conversation the client never saw.

   CONSECUTIVE HIDDEN TURNS SHARE ONE BAND. The stamp is drawn on the first turn
   of a run and suppressed on the rest, so the band reads as one continuous
   region of hidden work rather than as a stack of repeated labels.

   VISIBILITY IS THE ONE LOOKUP THAT MUST NEVER FALL THROUGH. An unrecognised
   value is refused rather than defaulted, because a default is a claim about
   whether the client can read the turn, and being wrong in either direction is
   the whole failure mode.
--------------------------------------------------------------------------- */

const AUTHOR_LABEL: Record<MessageAuthorKind, string> = {
  client: 'Client',
  expert: 'You',
  agent: 'Agent',
  system: 'Platform',
};

const AUTHOR_TONE: Record<MessageAuthorKind, string> = {
  client: 'text-ink-2',
  expert: 'text-hidden',
  agent: 'text-ink-3',
  system: 'text-ink-3',
};

export function Thread({
  messages,
  deltas,
  pendingAskId,
  onAnswerAsk,
  className,
}: {
  messages: readonly ChatMessage[];
  /** In-flight assistant text per message seq, before the canonical turn lands. */
  deltas: Record<number, string>;
  /** The ask the expert may answer right now, if any. */
  pendingAskId: string | null;
  onAnswerAsk: (ask: AskBlock) => void;
  className?: string;
}) {
  const streamingSeqs = React.useMemo(
    () => Object.keys(deltas).map(Number).filter((seq) => !messages.some((m) => m.seq === seq)),
    [deltas, messages],
  );

  if (messages.length === 0 && streamingSeqs.length === 0) {
    return (
      <Empty
        title="Nothing in this thread yet"
        body="The client's turns and yours both land here. Anything you write is invisible to them."
        className={className}
      />
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {messages.map((message, index) => {
        const previous = messages[index - 1];
        // Consecutive hidden turns share one band, so a run of expert work reads
        // as one continuous hidden region rather than a stack of repeated labels.
        const sameBand =
          previous !== undefined && previous.visibility !== 'client' && message.visibility !== 'client';

        return (
          <Turn
            key={message.id || message.seq}
            message={message}
            stamp={!sameBand}
            pendingAskId={pendingAskId}
            onAnswerAsk={onAnswerAsk}
          />
        );
      })}

      {streamingSeqs.map((seq) => (
        <StreamingTurn key={`delta-${seq}`} text={deltas[seq] ?? ''} />
      ))}
    </div>
  );
}

function Turn({
  message,
  stamp,
  pendingAskId,
  onAnswerAsk,
}: {
  message: ChatMessage;
  stamp: boolean;
  pendingAskId: string | null;
  onAnswerAsk: (ask: AskBlock) => void;
}) {
  if (!isKnownVisibility(message.visibility)) {
    return (
      <UnknownTurn
        issueKind="unknown_visibility"
        token={String(message.visibility).slice(0, 48)}
        context={{ seq: message.seq }}
      />
    );
  }

  const author = message.author_kind;
  if (!(author in AUTHOR_LABEL)) {
    return (
      <UnknownTurn issueKind="unknown_author_kind" token={String(author).slice(0, 48)} context={{ seq: message.seq }} />
    );
  }

  const visibility: Visibility = message.visibility;

  return (
    <VisibilityBand visibility={visibility} stamp={stamp}>
      <article className="min-w-0">
        <header className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <span className={cn('font-mono text-tiny uppercase tracking-[0.1em]', AUTHOR_TONE[author])}>
            {AUTHOR_LABEL[author]}
          </span>
          {message.author_display_name && author !== 'expert' ? (
            <span className="truncate text-xs text-ink-3">{message.author_display_name}</span>
          ) : null}
          <span
            className="font-mono text-tiny tabular-nums text-ink-3"
            title={formatDateTime(message.created_at)}
          >
            {formatClock(message.created_at)}
          </span>
          <span className="font-mono text-tiny text-ink-3">#{message.seq}</span>
          {message.run_id ? <span className="font-mono text-tiny text-ink-3">run {message.run_id}</span> : null}
        </header>

        <div className="mt-1.5 space-y-2">
          {message.blocks.map((block, index) => (
            <BlockView
              key={index}
              block={block}
              onAnswerAsk={
                (block as { type?: string }).type === 'ask' &&
                (block as AskBlock).ask_id === pendingAskId
                  ? onAnswerAsk
                  : undefined
              }
            />
          ))}
        </div>

        {message.attachments && message.attachments.length > 0 ? (
          <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-3 gap-y-1 p-0">
            {message.attachments.map((attachment) => (
              <li key={attachment.id} className="font-mono text-tiny text-ink-3" title={attachment.sha256}>
                {attachment.filename}
              </li>
            ))}
          </ul>
        ) : null}
      </article>
    </VisibilityBand>
  );
}

/**
 * Assistant text arriving as deltas, before the canonical `message` frame.
 *
 * Always hidden from the client while it is live: mid-run agent text belongs to
 * whichever run produced it, and during a review that is an expert run. The
 * canonical turn that replaces this carries the real visibility from the server,
 * so the optimistic render is the conservative one.
 */
function StreamingTurn({ text }: { text: string }) {
  return (
    <VisibilityBand visibility="internal">
      <p className="font-mono text-tiny uppercase tracking-[0.1em] text-ink-3">Agent · streaming</p>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-[19px] text-ink">
        {text}
        <span aria-hidden className="ml-0.5 inline-block h-3 w-[2px] animate-notch-pulse bg-action align-middle" />
      </p>
    </VisibilityBand>
  );
}
